/**
 * localStorageMigration
 *
 * Centralised one-time migration of legacy localStorage keys.
 * After a key is renamed or namespaced, register the mapping here
 * so old values are ported forward and cleaned up automatically.
 *
 * Migrations run once per browser and are skipped on subsequent loads.
 */

const MIGRATION_MARKER = "zooi.localStorageMigration.v1";

interface LegacyMapping {
  /** The old key that may exist in localStorage. */
  from: string;
  /** The new key the value should be moved to. */
  to: string;
  /** Optional transform applied to the raw string value during migration. */
  transform?: (raw: string) => string;
}

/** Add new mappings here when a key is renamed or namespaced. */
const LEGACY_MAPPINGS: LegacyMapping[] = [
  // useAutoRefresh keys were unprefixed before the `admin.autoRefresh:` namespace.
  // Matches any key that looks like an admin refresh setting.
  // These are handled dynamically below, but we keep explicit entries for known ones.
  { from: "admin.domainClaim.autoRefresh", to: "admin.autoRefresh:admin.domainClaim.autoRefresh" },
  { from: "admin.renderMeta.autoRefresh", to: "admin.autoRefresh:admin.renderMeta.autoRefresh" },
  { from: "admin.domainClaimFlow.autoRefresh", to: "admin.autoRefresh:admin.domainClaimFlow.autoRefresh" },
  { from: "admin.landingPageCrawl.autoRefresh", to: "admin.autoRefresh:admin.landingPageCrawl.autoRefresh" },
  { from: "admin.trackingRecon.autoRefresh", to: "admin.autoRefresh:admin.trackingRecon.autoRefresh" },
  { from: "admin.feedOptimizer.autoRefresh", to: "admin.autoRefresh:admin.feedOptimizer.autoRefresh" },
  { from: "admin.gmcErrorHistory.autoRefresh", to: "admin.autoRefresh:admin.gmcErrorHistory.autoRefresh" },
  { from: "admin.adsActionLog.autoRefresh", to: "admin.autoRefresh:admin.adsActionLog.autoRefresh" },
  { from: "admin.runLog.autoRefresh", to: "admin.autoRefresh:admin.runLog.autoRefresh" },
  { from: "admin.sitemapStatus.autoRefresh", to: "admin.autoRefresh:admin.sitemapStatus.autoRefresh" },
  { from: "admin.renderMetaMonitor.autoRefresh", to: "admin.autoRefresh:admin.renderMetaMonitor.autoRefresh" },
  { from: "admin.botUaMonitor.autoRefresh", to: "admin.autoRefresh:admin.botUaMonitor.autoRefresh" },
  { from: "admin.deploySmoke.autoRefresh", to: "admin.autoRefresh:admin.deploySmoke.autoRefresh" },
];

function getMigrationVersion(): number {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(MIGRATION_MARKER);
    return raw ? Number(raw) : 0;
  } catch {
    return 0;
  }
}

function setMigrationVersion(v: number): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(MIGRATION_MARKER, String(v));
  } catch { /* storage full or blocked */ }
}

function scanDynamicLegacyKeys(): LegacyMapping[] {
  if (typeof window === "undefined") return [];
  const found: LegacyMapping[] = [];
  const nsPrefix = "admin.autoRefresh:";
  const knownPrefixes = ["admin."];
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (!key) continue;
    // Catch any remaining unprefixed admin.*.autoRefresh keys not explicitly listed
    if (
      key.endsWith(".autoRefresh") &&
      !key.startsWith(nsPrefix) &&
      knownPrefixes.some((p) => key.startsWith(p))
    ) {
      const to = `${nsPrefix}${key}`;
      if (to !== key) {
        found.push({ from: key, to });
      }
    }
  }
  return found;
}

export interface MigrationResult {
  migrated: number;
  removed: number;
  skipped: number;
  details: string[];
}

/**
 * Runs all registered migrations once per browser.
 * Safe to call multiple times — idempotent via MIGRATION_MARKER.
 */
export function runLocalStorageMigration(): MigrationResult {
  const result: MigrationResult = { migrated: 0, removed: 0, skipped: 0, details: [] };

  if (typeof window === "undefined") return result;

  const currentVersion = getMigrationVersion();
  const TARGET_VERSION = 1;
  if (currentVersion >= TARGET_VERSION) {
    result.details.push("Migration already up to date.");
    return result;
  }

  const allMappings = [...LEGACY_MAPPINGS, ...scanDynamicLegacyKeys()];
  const seenFrom = new Set<string>();

  for (const { from, to, transform } of allMappings) {
    if (seenFrom.has(from)) continue;
    seenFrom.add(from);

    try {
      const raw = window.localStorage.getItem(from);
      if (raw == null) {
        result.skipped++;
        continue;
      }

      // Don't overwrite an existing new key unless the old key has a value and the new doesn't
      const existingNew = window.localStorage.getItem(to);
      if (existingNew != null && existingNew !== raw) {
        // New key already set to something else — keep it, just remove legacy
        window.localStorage.removeItem(from);
        result.removed++;
        result.details.push(`Removed legacy "${from}" (new key "${to}" already exists with different value)`);
        continue;
      }

      const migratedValue = transform ? transform(raw) : raw;
      window.localStorage.setItem(to, migratedValue);
      window.localStorage.removeItem(from);
      result.migrated++;
      result.details.push(`Migrated "${from}" → "${to}"`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      result.details.push(`Failed to migrate "${from}": ${msg}`);
    }
  }

  // Also clean up any completely orphaned keys we know are no longer used
  const ORPHANED_KEYS: string[] = [
    // Add retired keys here when features are removed
  ];
  for (const orphan of ORPHANED_KEYS) {
    try {
      if (window.localStorage.getItem(orphan) != null) {
        window.localStorage.removeItem(orphan);
        result.removed++;
        result.details.push(`Removed orphaned "${orphan}"`);
      }
    } catch { /* ignore */ }
  }

  setMigrationVersion(TARGET_VERSION);
  return result;
}

/**
 * Dev-time helper: inspect the current localStorage and report potential legacy keys.
 * Call from browser console: `__zooiInspectLocalStorage()`
 */
export function inspectLocalStorageLegacy(): void {
  if (typeof window === "undefined") return;
  const candidates: string[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (!key) continue;
    if (
      !key.startsWith("admin.autoRefresh:") &&
      key.includes("autoRefresh")
    ) {
      candidates.push(key);
    }
  }
  // eslint-disable-next-line no-console
  console.log("[localStorageMigration] Potential legacy keys:", candidates);
}

// Expose for console debugging
if (typeof window !== "undefined") {
  (window as unknown as Record<string, unknown>).__zooiInspectLocalStorage = inspectLocalStorageLegacy;
}
